import {
  LegacyProductCode,
  ProductBehavior,
} from '@up2stay/up2promo-api-client';
import type { ProductDto } from '@up2stay/up2promo-api-client';

import {
  isLocationBackedProductType,
  isLocationBehaviourType,
  parseInstagramUsername,
  parseTiktokUsername,
} from './product-routing.utils';

import { resolveBehaviorProductType } from './product-identity.utils';

export type ProductCardAssignment =
  | { kind: 'profile'; userId: string }
  | { kind: 'location'; locationId: string }
  | { kind: 'target'; label: string }
  | { kind: 'empty'; reason: 'profile' | 'location' | 'generic' };

function formatTargetLabel(
  product: Pick<ProductDto, 'target' | 'item'>,
  targetUrl: string,
): string {
  const behavior = resolveBehaviorProductType(product);
  if (behavior === LegacyProductCode.IG) {
    const username = parseInstagramUsername(targetUrl);
    if (username) return `@${username}`;
  }
  if (behavior === LegacyProductCode.TT) {
    const username = parseTiktokUsername(targetUrl);
    if (username) return `@${username}`;
  }

  try {
    const parsed = new URL(
      targetUrl.startsWith('http') ? targetUrl : `https://${targetUrl}`,
    );
    const host = parsed.hostname.replace(/^www\./, '');
    const path =
      parsed.pathname === '/'
        ? ''
        : parsed.pathname.replace(/\/$/, '').slice(0, 28);
    return path ? `${host}${path}` : host;
  } catch {
    return targetUrl;
  }
}

/** What the products list should highlight as the product’s assignment. */
export function resolveProductCardAssignment(
  product: Pick<ProductDto, 'target' | 'item'>,
): ProductCardAssignment {
  const behavior = resolveBehaviorProductType(product);
  const { target } = product;
  const userId = target?.userId?.trim() || '';
  const locationId = target?.locationId?.trim() || '';
  const targetUrl = target?.url?.trim() || '';

  const isProfile =
    behavior === LegacyProductCode.BC ||
    target?.kind === ProductBehavior.PROFILE;

  if (isProfile) {
    return userId
      ? { kind: 'profile', userId }
      : { kind: 'empty', reason: 'profile' };
  }

  if (locationId) {
    return { kind: 'location', locationId };
  }

  if (targetUrl) {
    return {
      kind: 'target',
      label: formatTargetLabel(product, targetUrl),
    };
  }

  if (
    isLocationBackedProductType(behavior) ||
    isLocationBehaviourType(target?.kind)
  ) {
    return { kind: 'empty', reason: 'location' };
  }

  return { kind: 'empty', reason: 'generic' };
}
